/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  isAddNewIPInterfaceSelector,
  isAddNewLanDeviceSelector,
  lanDeviceIndexesSelector,
  lanDeviceListSelector,
  selectedLanDeviceSelector,
} from "../../../store/api/deviceConfig/selectors";
import { IIPInterface, ILanDevice } from "../../../store/api/deviceConfig/types";
import { getNextIPInterfaceIndex, getNextLANDeviceIndex, prepareIPInterfacePayload, prepareLANDevicePayload } from "../helpers";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { invalidStr, OPERATIONTYPES } from "../../../globals/constants";
import { useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor, setConfirmDialog, setOpenCustomDailog, setOpenToast } from "../../../store/helpers/helperReducers";
import { isIPv4, isIPv4SubnetMask, isIPv6, isIPv6SubnetMask, validate, validateIP, validateSubnet } from "../../../utils/validations";
import { setIPInterfaceList, setLanDeviceList } from "../../../store/api/deviceConfig/reducers";
import { DIALOGOPENFOR } from "../../../globals/globals";
import { errorMessage } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
interface IPayload {
  [key: string]: string;
}

export const useLanDeviceData = (selectedLanDevice?: ILanDevice) => {
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const [lanDeviceData, setLanDeviceData] = useState(selectedLanDevice);
  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [anchorElLanDevice, setAnchorElLanDevice] = useState<null | HTMLElement>(null);
  const [lanDeviceLoader, setLanDeviceLoader] = useState(false);

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  let lanDeviceList: ILanDevice[] = useSelector(lanDeviceListSelector);
  const isAddNewLanDevice = useSelector(isAddNewLanDeviceSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const lanDeviceIndexes = useSelector(lanDeviceIndexesSelector);

  const nextLanDeviceIndex = getNextLANDeviceIndex(lanDeviceIndexes, 10); // second parametere (10) is max index number of LAN Device

  useEffect(() => {
    if (isAddNewLanDevice) {
      setLanDeviceData({
        ...selectedLanDevice,
        index: nextLanDeviceIndex,
        name: `LANDevice.${nextLanDeviceIndex}`,
        status: "InProgress",
        deviceId: nextLanDeviceIndex,
      });
    } else {
      setLanDeviceData(selectedLanDevice);
    }
  }, [isAddNewLanDevice, nextLanDeviceIndex, selectedLanDevice]);

  const onblureHandler = () => {
    if (lanDeviceIndexes?.length > 0) {
      if (lanDeviceIndexes?.includes(lanDeviceData?.index)) {
        setErrMsg({ ...errMsg, index: `Port ${lanDeviceData?.index} is already in use.` });
        return;
      }
      if (!!lanDeviceData?.index && Number(lanDeviceData?.index) > 10) {
        setErrMsg({ ...errMsg, index: `Port limit is 1-10.` });
        return;
      }
    }
    if (nextLanDeviceIndex !== lanDeviceData?.index) {
      setLanDeviceData({ ...lanDeviceData, name: `LANDevice.${lanDeviceData?.index}` });
    }
  };

  const submitHandler = async () => {
    if (!!lanDeviceData) {
      if (Object.keys(lanDeviceData).length <= 0) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Please fill the required fields!" }));
        return;
      }
      onblureHandler();
      const { errorMessages, isValid } = validate(lanDeviceData);
      setErrMsg({ ...errMsg, ...errorMessages });

      if (isValid) {
        const operationType = isAddNewLanDevice ? OPERATIONTYPES.CREATE : OPERATIONTYPES.UPDATE;
        const lanDevicePayload =
          !!lanDeviceData &&
          !!selectedDevice?.objectKey &&
          prepareLANDevicePayload(lanDeviceData, selectedLanDevice, selectedDevice?.objectKey, operationType);
        if (!!lanDeviceData) {
          lanDeviceList = [lanDeviceData, ...lanDeviceList];
        }
        try {
          let result: any;
          dispatch(setLanDeviceList(lanDeviceList));
          setAnchorElLanDevice(null);

          setLanDeviceLoader(true);
          result = await setDeviceConfig(lanDevicePayload);
          if (result?.error || configStatus?.isError)
            throw result?.error;

          if (result.data?.status === 200) {
            setLanDeviceLoader(false);
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `The data has been submited`,
              })
            );
            setTimeout(() => {
              dispatch(setOpenCustomDailog(false));
            }, 500);
          }
        } catch (error: any) {
          setLanDeviceLoader(false);
          let lastAdded = lanDeviceList.filter((item) => item.index === lanDeviceData.index)[0];
          lastAdded = { ...lastAdded, status: "Failed" };
          const updateToLANDeviceList = lanDeviceList.filter((item) => item.index !== lanDeviceData.index);
          dispatch(setLanDeviceList([lastAdded, ...updateToLANDeviceList]));
          setTimeout(() => {
            const removedFailed = lanDeviceList.filter((item) => item.index !== lanDeviceData.index);
            dispatch(setLanDeviceList(removedFailed));
          }, 5000);
          dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
        }
      }
    }
  };
  const deleteLanDeviceHandler = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.LANDEVICE));
    setAnchorElLanDevice(null);
  };

  return {
    permissions,
    lanDeviceData,
    setLanDeviceData,
    isAddNewLanDevice,
    submitHandler,
    onblureHandler,
    errMsg,
    setErrMsg,
    deleteLanDeviceHandler,
    anchorElLanDevice,
    setAnchorElLanDevice,
    lanDeviceLoader,
  };
};

export const useIPInterfaceData = (selectedIPInterface: IIPInterface) => {
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const [ipInterfaceData, setIPInterfaceData] = useState(selectedIPInterface);
  const [anchorElIP, setAnchorElIP] = useState<null | HTMLElement>(null);

  const selectedDevice = useSelector(selectedDeviceSelector);
  const isAddNewIPInterface = useSelector(isAddNewIPInterfaceSelector);
  const selectedLanDevice = useSelector(selectedLanDeviceSelector);
  const lanDeviceList: ILanDevice[] = useSelector(lanDeviceListSelector);
  const [errMsg, setErrMsg] = useState<IPayload>({});

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  const newIPInterfaceIndex = selectedLanDevice?.ipInterfaces?.length > 0 ? getNextIPInterfaceIndex(selectedLanDevice?.ipInterfaces) : "1";

  useEffect(() => {
    if (isAddNewIPInterface && !!selectedLanDevice) {
      setIPInterfaceData({ ...ipInterfaceData, index: newIPInterfaceIndex, vlanId: selectedLanDevice.index });
    } else {
      !!selectedLanDevice && setIPInterfaceData({ ...selectedIPInterface, vlanId: selectedLanDevice.index });
    }
  }, []);

  const blurHandler = (event: any) => {
    const { name, value } = event.target;
    const { subnet, ipAddress } = invalidStr;

    if (name === "ipInterfaceIpAddress") {
      if (!!value && value.trim() === "0.0.0.0") {
        setErrMsg({ ...errMsg, [name]: ipAddress.invalid });
        return;
      }
      const { isValidIP, errorMessages } = validateIP(value);
      if (isValidIP) {
        setErrMsg({ ...errMsg, ipInterfaceSubnetMask: "" });
      } else {
        setErrMsg({ ...errMsg, [name]: errorMessages });
      }
    } else if (name === "ipInterfaceSubnetMask") {
      if (!!value && value.trim() === "0.0.0.0") {
        setErrMsg({ ...errMsg, [name]: subnet.invalid });
      } else {
        const isValidIPv4 = isIPv4(ipInterfaceData.ipInterfaceIpAddress);
        const isValidIPv6 = isIPv6(ipInterfaceData.ipInterfaceIpAddress);
        if (!!value && isValidIPv4) {
          const isValidSubnet = isIPv4SubnetMask(value);
          !isValidSubnet && setErrMsg({ ...errMsg, [name]: subnet.invalid });
        } else if (!!value && isValidIPv6) {
          const isValidSubnet6 = isIPv6SubnetMask(value);
          !isValidSubnet6 && setErrMsg({ ...errMsg, [name]: subnet.invalid });
        } else {
          setErrMsg({ ...errMsg, ipInterfaceSubnetMask: subnet.required });
        }
      }
    } else {
      if (name !== "description") {
        const { errorMessages, isValid } = validate({ [name]: value });
        !isValid && setErrMsg({ ...errMsg, ...errorMessages });
      }
    }
  };

  // Function to add or update an ipInterface in the selected lanDevice
  const handleIpInterface = (ipInterfaceData: IIPInterface, deviceIndex: string, interfaceIndex: string, type: string) => {
    const updatedList = lanDeviceList.map((device) => {
      if (device.index === deviceIndex && Array.isArray(device?.ipInterfaces)) {
        if (type === OPERATIONTYPES.CREATE) {
          const updatedInterfaces = [...device.ipInterfaces, ipInterfaceData];
          return { ...device, ipInterfaces: updatedInterfaces };
        } else if (type === OPERATIONTYPES.UPDATE) {
          const updatedInterfaces = device.ipInterfaces.map((ipInterface) => {
            return ipInterface?.index === interfaceIndex ? ipInterfaceData : ipInterface;
          });
          return { ...device, ipInterfaces: updatedInterfaces };
        }
      }
      return device;
    });

    // Update the lanDeviceList with the new data
    dispatch(setLanDeviceList(updatedList));
  };

  const submitHandler = async () => {
    if (!!ipInterfaceData) {
      if (Object.keys(ipInterfaceData).length <= 0) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Please fill the required fields!" }));
        return;
      }
      // delete ipInterfaceData.operState;
      // const { errorMessages, isValid } = validate(ipInterfaceData);
      // setErrMsg({ ...errMsg, ...errorMessages });

      // if (isValid) {
      const oldLanDeviceList = [...lanDeviceList];
      const operationType = isAddNewIPInterface ? OPERATIONTYPES.CREATE : OPERATIONTYPES.UPDATE;
      let oldIPinterface;
      if (selectedIPInterface?.vlanId === "0") {
        oldIPinterface = { ...selectedIPInterface, vlanId: selectedLanDevice.index };
      } else {
        oldIPinterface = selectedIPInterface;
      }
      const ipInterfacePayload =
        !!ipInterfaceData && prepareIPInterfacePayload(ipInterfaceData, oldIPinterface, selectedDevice?.objectKey, operationType);

      try {
        let result: any;
        setAnchorElIP(null);
        dispatch(setBasicDialogOpenFor(undefined));
        handleIpInterface(ipInterfaceData, selectedLanDevice.index, ipInterfaceData.index, operationType);
        //dispatch(setIPInterfaceList({ ipInterfaceData, deviceIndex: selectedLanDevice.index, operationType: operationType }));

        result = await setDeviceConfig(ipInterfacePayload);
        if (result?.error || configStatus?.isError) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `The data has been submited`,
            })
          );
        }
      } catch (error: any) {
        setAnchorElIP(null);
        setTimeout(() => {
          dispatch(setLanDeviceList(oldLanDeviceList));
          //dispatch(setIPInterfaceList({ ipInterfaceData, deviceIndex: selectedLanDevice.index, operationType: OPERATIONTYPES.UPDATE }));
        }, 2000);
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: JSON.stringify(error?.data) || "Something went wrong!" }));
      }
    }
  };
  const deleteIPInterfaceHandler = () => {
    dispatch(setConfirmDialog(DIALOGOPENFOR.IPINTERFACE));
    setAnchorElIP(null);
  };
  return {
    permissions,
    ipInterfaceData,
    setIPInterfaceData,
    isAddNewIPInterface,
    submitHandler,
    errMsg,
    setErrMsg,
    deleteIPInterfaceHandler,
    anchorElIP,
    setAnchorElIP,
    blurHandler,
  };
};
